'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';
import { CreditCard as CreditCardIcon } from '@phosphor-icons/react/dist/ssr/CreditCard';
import { GearSix as GearSixIcon } from '@phosphor-icons/react/dist/ssr/GearSix';
import { LockKey as LockKeyIcon } from '@phosphor-icons/react/dist/ssr/LockKey';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';

import config from '@src/config';
import paths from '@src/paths';
import { RouterLink } from '@src/components/core/link';
import { applyDefaultSettings, setSettings as setPersistedSettings } from '@src/corelib';
import { useSettings } from '@src/hooks';
import { SettingsDrawer } from '@src/components/core/settings/settings-drawer';

import { useAuth } from '@src/context';

export default function UserPopover({ anchorEl, onClose, open }) {
  const { logout, state } = useAuth();
  const { settings, setSettings } = useSettings();
  const { setColorScheme } = useColorScheme();
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const handleSettingsUpdate = async (values) => {
    if (values.colorScheme) setColorScheme(values.colorScheme);
    const updated = { ...settings, ...values };
    setPersistedSettings(updated);
    setSettings(updated);
  };
  const handleSettingsReset = async () => {
    setColorScheme(config.SITE.colorScheme);
    setPersistedSettings({});
    setSettings(applyDefaultSettings({}));
  };

  const authUser = state?.user || {};
  const normalizeSlug = (value = '') =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const getMembershipDashboardSlug = (membership = {}) => {
    const clientSlug = membership?.client_slug || membership?.client_id;
    if (clientSlug) {
      return clientSlug;
    }
    const parentName = membership?.parent_company_name;
    if (parentName) {
      return normalizeSlug(parentName);
    }
    return membership?.parent_company_id || null;
  };
  const isClientMember =
    authUser &&
    !authUser.is_staff &&
    !authUser.is_superuser &&
    Array.isArray(authUser.client_memberships) &&
    authUser.client_memberships.length > 0;
  const defaultMembership = isClientMember ? authUser.client_memberships[0] : null;
  const dashboardSlug = defaultMembership
    ? getMembershipDashboardSlug(defaultMembership)
    : null;
  const displayName = [authUser.first_name, authUser.last_name].filter(Boolean).join(' ') || authUser.email || 'User';
  const email = authUser.email || '';
  return (
    <React.Fragment>
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      onClose={onClose}
      open={Boolean(open)}
      slotProps={{ paper: { sx: { width: '280px' } } }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <Box sx={{ p: 2 }}>
        <Typography>{displayName}</Typography>
        <Typography color="text.secondary" variant="body2">
          {email}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ p: 1 }}>
        {dashboardSlug ? (
          <MenuItem component={RouterLink} href={paths.clientDashboard(dashboardSlug)} onClick={onClose}>
            <ListItemIcon>
              <UserIcon />
            </ListItemIcon>
            Dashboard
          </MenuItem>
        ) : null}
        <MenuItem component={RouterLink} href={paths.dashboard.settings.account} onClick={onClose}>
          <ListItemIcon>
            <UserIcon />
          </ListItemIcon>
          Account
        </MenuItem>
        {!isClientMember ? (
          <MenuItem component={RouterLink} href={paths.dashboard.settings.security} onClick={onClose}>
            <ListItemIcon>
              <LockKeyIcon />
            </ListItemIcon>
            Security
          </MenuItem>
        ) : null}
        {!isClientMember ? (
          <MenuItem component={RouterLink} href={paths.dashboard.settings.billing} onClick={onClose}>
            <ListItemIcon>
              <CreditCardIcon />
            </ListItemIcon>
            Billing
          </MenuItem>
        ) : null}
        <MenuItem
          component="div"
          onClick={() => {
            setSettingsOpen(true);
            onClose?.();
          }}
        >
          <ListItemIcon>
            <GearSixIcon />
          </ListItemIcon>
          Preferences
        </MenuItem>
      </List>
      <Divider />
      <Box sx={{ p: 1 }}>
        <MenuItem component="div" onClick={logout} sx={{ justifyContent: 'center' }}>
          Sign out
        </MenuItem>
      </Box>
    </Popover>
    <SettingsDrawer
      onClose={() => setSettingsOpen(false)}
      onReset={handleSettingsReset}
      onUpdate={handleSettingsUpdate}
      open={settingsOpen}
      values={settings}
    />
    </React.Fragment>
  );
}
